// Registerpage.jsx — full mobile-parity onboarding on the right panel.
// Flow: email → otp → password → resume → howHeard → jobSearchStatus →
// location → contactDetails → voluntaryDisclosures → welcomeComplete.
// Left panel stays constant: <AuthLeftPanel />.
import { useEffect, useRef, useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import {
  auth,
  googleProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
} from "../firebaseConfig"
import { doc, serverTimestamp, setDoc } from "firebase/firestore"
import { db } from "../firebaseConfig"
import { fetchSignInMethodsForEmail } from "firebase/auth"
import mixpanel from "mixpanel-browser"
import resolutLogo from "../assets/resolut-logo.png"
import AuthLeftPanel from "../components/AuthLeftPanel"
import { useOnboarding } from "../contexts/OnboardingContext"
import ResumeUploadStep       from "../components/onboarding/ResumeUploadStep"
import HowHeardStep           from "../components/onboarding/HowHeardStep"
import JobSearchStatusStep    from "../components/onboarding/JobSearchStatusStep"
import LocationStep           from "../components/onboarding/LocationStep"
import ContactDetailsStep     from "../components/onboarding/ContactDetailsStep"
import VoluntaryDisclosuresStep from "../components/onboarding/VoluntaryDisclosuresStep"
import WelcomeCompleteStep    from "../components/onboarding/WelcomeCompleteStep"

const FF = '"Plus Jakarta Sans", "DM Sans", -apple-system, BlinkMacSystemFont, sans-serif'
const API_BASE = "https://jobscanner-e1dd4.ue.r.appspot.com"
const OTP_LENGTH = 6

const C = {
  bg:         "#FAFAFA",
  card:       "#FFFFFF",
  cardSoft:   "#F4F4F5",
  border:     "#E4E4E7",
  borderFoc:  "#18181b",
  text:       "#18181b",
  textMuted:  "#52525b",
  textDim:    "#71717a",
  textFaint:  "#a1a1aa",
  accent:     "#18181b",
  accentHov:  "#3f3f46",
  accentText: "#FFFFFF",
  ember:      "#F25A20",
  successBg:  "#ECFDF3",
  successBorder: "#C7EDD4",
  successInk: "#166534",
  errorBg:    "#FEF2F2",
  errorBorder:"#FECACA",
  errorInk:   "#B91C1C",
}

// Steps in order
const STEP = {
  email:      0,
  otp:        1,
  password:   2,
  resume:     3,
  howHeard:   4,
  jobSearch:  5,
  location:   6,
  contact:    7,
  voluntary:  8,
  complete:   9,
}

/* ── helpers ──────────────────────────────────────────────────────── */

const useIsMobile = () => {
  const [v, setV] = useState(() => typeof window !== "undefined" && window.innerWidth < 900)
  useEffect(() => {
    const h = () => setV(window.innerWidth < 900)
    window.addEventListener("resize", h)
    return () => window.removeEventListener("resize", h)
  }, [])
  return v
}

const Spinner = ({ color = "#FFFFFF" }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: "rg-spin 0.8s linear infinite" }}>
    <circle cx="12" cy="12" r="10" stroke={color + "44"} strokeWidth="2.5" />
    <path d="M12 2a10 10 0 0110 10" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
  </svg>
)

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
)

const EyeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
    <path d="M1 10s3.6-6 9-6 9 6 9 6-3.6 6-9 6-9-6-9-6z" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.4" />
  </svg>
)
const EyeOffIcon = () => (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
    <path d="M3 3l14 14M8.5 8.6A2.5 2.5 0 0012.4 12M6.5 5.6C4.5 6.8 2.8 8.7 1 10c1.6 2 5 6 9 6a8.5 8.5 0 004.5-1.5M9 4.1A8.4 8.4 0 0110 4c4 0 7.4 4 9 6a14 14 0 01-2.1 2.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
)

function LightInput({ label, error, right, ...props }) {
  const [focused, setFocused] = useState(false)
  return (
    <div>
      {label && (
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.text, fontFamily: FF, marginBottom: 7 }}>
          {label}
        </label>
      )}
      <div style={{
        display: "flex", alignItems: "center", height: 46,
        background: C.card,
        border: `1px solid ${error ? C.errorBorder : focused ? C.borderFoc : C.border}`,
        borderRadius: 10,
        transition: "border-color 0.15s, box-shadow 0.15s",
        boxShadow: focused ? "0 0 0 3px rgba(24,24,27,0.06)" : "none",
        overflow: "hidden", position: "relative",
      }}>
        <input
          style={{
            flex: 1, border: "none", background: "transparent",
            fontSize: 14, fontWeight: 500, color: C.text,
            outline: "none", fontFamily: FF, height: "100%",
            paddingLeft: 14, paddingRight: right ? 44 : 14,
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          {...props}
        />
        {right}
      </div>
      {error && (
        <p style={{ margin: "6px 0 0", fontSize: 12, fontWeight: 500, color: C.errorInk, fontFamily: FF }}>{error}</p>
      )}
    </div>
  )
}

/* ── OTP input ────────────────────────────────────────────────────── */

const OtpInput = ({ value, onChange, onComplete }) => {
  const inputs = useRef([])
  const digits = Array.from({ length: OTP_LENGTH }, (_, i) => value[i] || "")

  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, "").slice(-1)
    const next = [...digits]
    next[idx] = val
    const joined = next.join("")
    onChange(joined)
    if (val && idx < OTP_LENGTH - 1) setTimeout(() => inputs.current[idx + 1]?.focus(), 0)
    if (joined.replace(/\s/g, "").length === OTP_LENGTH) onComplete?.(joined)
  }
  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      if (digits[idx]) {
        const n = [...digits]; n[idx] = ""; onChange(n.join(""))
      } else if (idx > 0) {
        const n = [...digits]; n[idx - 1] = ""; onChange(n.join(""))
        setTimeout(() => inputs.current[idx - 1]?.focus(), 0)
      }
      e.preventDefault()
    } else if (e.key === "ArrowLeft" && idx > 0) inputs.current[idx - 1]?.focus()
    else if (e.key === "ArrowRight" && idx < OTP_LENGTH - 1) inputs.current[idx + 1]?.focus()
  }
  const handlePaste = (e) => {
    e.preventDefault()
    const p = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH)
    const next = Array.from({ length: OTP_LENGTH }, (_, i) => p[i] || "")
    const joined = next.join("")
    onChange(joined)
    setTimeout(() => inputs.current[Math.min(p.length, OTP_LENGTH - 1)]?.focus(), 0)
    if (p.length === OTP_LENGTH) onComplete?.(joined)
  }

  return (
    <div style={{ display: "flex", gap: 10 }}>
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => (inputs.current[i] = el)}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(e, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          onPaste={handlePaste}
          onFocus={(e) => {
            e.target.style.borderColor = C.accent
            e.target.style.boxShadow = "0 0 0 3px rgba(24,24,27,0.08)"
            e.target.select()
          }}
          onBlur={(e) => {
            e.target.style.borderColor = digit ? C.accent : C.border
            e.target.style.boxShadow = "none"
          }}
          style={{
            width: 50, height: 58,
            textAlign: "center", fontSize: 20, fontWeight: 700, fontFamily: FF,
            color: C.text, background: C.card,
            border: `1px solid ${digit ? C.accent : C.border}`,
            borderRadius: 10, outline: "none",
            transition: "border-color 0.12s, box-shadow 0.12s",
          }}
        />
      ))}
    </div>
  )
}

/* ── primary / ghost buttons ──────────────────────────────────────── */

const AccentBtn = ({ children, loading, disabled, type = "button", onClick }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={loading || disabled}
    style={{
      width: "100%", height: 46,
      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
      background: loading || disabled ? "#a1a1aa" : C.accent,
      color: C.accentText,
      border: "none", borderRadius: 10,
      fontSize: 14, fontWeight: 600, fontFamily: FF,
      cursor: loading || disabled ? "not-allowed" : "pointer",
      transition: "background 0.15s",
    }}
    onMouseEnter={(e) => { if (!loading && !disabled) e.currentTarget.style.background = C.accentHov }}
    onMouseLeave={(e) => { if (!loading && !disabled) e.currentTarget.style.background = C.accent }}
  >
    {loading ? <Spinner color="#fff" /> : children}
  </button>
)

const GhostBtn = ({ children, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      width: "100%", height: 46,
      display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 10,
      fontSize: 14, fontWeight: 500, fontFamily: FF, color: C.text,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.6 : 1,
      transition: "border-color 0.15s, background 0.15s",
    }}
    onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.borderColor = C.textDim; e.currentTarget.style.background = "#FAFAFA" } }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.card }}
  >
    {children}
  </button>
)

const ErrBanner = ({ msg }) => (
  <div style={{
    marginBottom: 18, padding: "11px 14px",
    background: C.errorBg, border: `1px solid ${C.errorBorder}`,
    borderRadius: 10, display: "flex", alignItems: "flex-start", gap: 10,
  }}>
    <svg width="15" height="15" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
      <circle cx="10" cy="10" r="8" stroke={C.errorInk} strokeWidth="1.5" />
      <path d="M10 6v4M10 14h.01" stroke={C.errorInk} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
    <span style={{ fontSize: 13, fontWeight: 500, color: C.errorInk, fontFamily: FF }}>{msg}</span>
  </div>
)

const OkBanner = ({ msg }) => (
  <div style={{
    padding: "11px 14px",
    background: C.successBg, border: `1px solid ${C.successBorder}`,
    borderRadius: 10, display: "flex", alignItems: "center", gap: 10,
  }}>
    <svg width="15" height="15" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="10" cy="10" r="8" stroke={C.successInk} strokeWidth="1.5" />
      <path d="M7 10l2.5 2.5 4-4" stroke={C.successInk} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    <span style={{ fontSize: 13, fontWeight: 500, color: C.successInk, fontFamily: FF }}>{msg}</span>
  </div>
)

/* ── 3-dot indicator for sign-up stages (email/otp/password) ─────── */

function SignupDots({ step }) {
  return (
    <div style={{ display: "flex", gap: 5 }}>
      {[0, 1, 2].map((i) => {
        const active = i === step
        const done   = i < step
        return (
          <div key={i} style={{
            width: active ? 20 : 6, height: 6, borderRadius: 3,
            background: done ? "rgba(24,24,27,0.35)" : active ? C.accent : "#DDDAD5",
            transition: "all 0.3s ease",
          }} />
        )
      })}
    </div>
  )
}

function friendlyError(code) {
  const m = {
    "auth/email-already-in-use": "An account with this email already exists.",
    "auth/invalid-email":        "Please enter a valid email address.",
    "auth/weak-password":        "Password is too weak.",
    "auth/too-many-requests":    "Too many attempts. Please wait a moment.",
    "auth/popup-closed-by-user": "Google sign-in was cancelled.",
    "auth/network-request-failed":"Network error. Check your connection.",
  }
  return m[code] || "Something went wrong. Please try again."
}

/* ══════════════════════════════════════════════════════════════════
   MAIN
   ══════════════════════════════════════════════════════════════════ */
export default function RegisterPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useIsMobile()
  const { initProfile, profile } = useOnboarding()

  // Allow landing directly at the resume step (used by Login redirect).
  const initialStep = (() => {
    const s = typeof location.state?.step === "number" ? location.state.step : null
    if (s != null && s >= 0 && s <= STEP.complete) return s
    const sp = new URLSearchParams(location.search).get("resume")
    if (sp === "1") return STEP.resume
    return STEP.email
  })()

  const [step, setStep]           = useState(initialStep)
  const [email, setEmail]         = useState(location.state?.email || "")
  const [otp, setOtp]             = useState("")
  const [retrySeconds, setRetry]  = useState(0)
  const [password, setPassword]   = useState("")
  const [confirmPw, setConfirm]   = useState("")
  const [showPw, setShowPw]       = useState(false)
  const [errors, setErrors]       = useState({})
  const [globalErr, setGlobalErr] = useState("")
  const [loading, setLoading]     = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)

  // If we're starting at the resume step but have no profile yet (e.g. user
  // refreshed), initialize from the current firebase user.
  useEffect(() => {
    if (step >= STEP.resume && !profile && auth.currentUser) {
      initProfile(auth.currentUser.uid, auth.currentUser.email)
    }
  }, [step])

  useEffect(() => {
    if (retrySeconds <= 0) return
    const t = setTimeout(() => setRetry((s) => s - 1), 1000)
    return () => clearTimeout(t)
  }, [retrySeconds])

  // ── API helpers ──
  const sendOtp = async (addr) => {
    const r = await fetch(`${API_BASE}/verification/send`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: addr }),
    })
    const d = await r.json().catch(() => ({}))
    if (!r.ok) throw new Error(d?.error || "Failed to send code")
  }
  const verifyOtp = async (addr, code) => {
    const r = await fetch(`${API_BASE}/verification/verify`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: addr, otp: code }),
    })
    const d = await r.json().catch(() => ({}))
    if (!r.ok) throw new Error(d?.error || "Verification failed")
    return d
  }

  // ── handlers ──
  const handleEmailSubmit = async (e) => {
    e?.preventDefault()
    setGlobalErr("")
    if (!email.includes("@")) {
      setErrors({ email: "Enter a valid email address" }); return
    }
    setLoading(true)
    try {
      try {
        const methods = await fetchSignInMethodsForEmail(auth, email.trim())
        if (methods && methods.length > 0) {
          navigate("/login", { state: { email: email.trim(), fromRegister: true } })
          return
        }
      } catch { /* ignore — proceed to send OTP */ }
      await sendOtp(email.trim())
      setRetry(45)
      setStep(STEP.otp)
    } catch (err) {
      setGlobalErr(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleOtpVerify = async (codeArg) => {
    const code = codeArg ?? otp
    setGlobalErr("")
    if (code.length < OTP_LENGTH) {
      setErrors({ otp: "Enter the full 6-digit code" }); return
    }
    setLoading(true)
    try {
      await verifyOtp(email.trim(), code)
      setStep(STEP.password)
    } catch (err) {
      setGlobalErr(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    if (retrySeconds > 0) return
    setOtp(""); setGlobalErr("")
    try {
      await sendOtp(email.trim())
      setRetry(45)
    } catch (err) {
      setGlobalErr(err.message)
    }
  }

  const handlePasswordSubmit = async (e) => {
    e?.preventDefault()
    const err = {}
    if (password.length < 6)      err.password = "Minimum 6 characters"
    if (password !== confirmPw)   err.confirm  = "Passwords don't match"
    setErrors(err)
    if (Object.keys(err).length > 0) return

    setLoading(true); setGlobalErr("")
    try {
      const cred = await createUserWithEmailAndPassword(auth, email.trim(), password)
      // Seed users/{uid} doc so dashboards see an entry immediately.
      await setDoc(doc(db, "users", cred.user.uid), {
        uid: cred.user.uid,
        email: cred.user.email || email.trim(),
        createdAt: serverTimestamp(),
        onboardingComplete: false,
      }, { merge: true })
      initProfile(cred.user.uid, cred.user.email || email.trim())
      mixpanel.track("Account Created", { email: email.trim(), method: "email" })
      setStep(STEP.resume)
    } catch (err) {
      setGlobalErr(friendlyError(err.code))
    } finally {
      setLoading(false)
    }
  }

  const handleGoogle = async () => {
    setGoogleLoading(true); setGlobalErr("")
    try {
      const cred = await signInWithPopup(auth, googleProvider)
      // Create or merge user doc.
      await setDoc(doc(db, "users", cred.user.uid), {
        uid: cred.user.uid,
        email: cred.user.email || "",
        name: cred.user.displayName || undefined,
        profileUrl: cred.user.photoURL || undefined,
        createdAt: serverTimestamp(),
      }, { merge: true })
      initProfile(cred.user.uid, cred.user.email || "")
      mixpanel.track("Account Created", { method: "google" })
      setStep(STEP.resume)
    } catch (err) {
      if (err?.code !== "auth/popup-closed-by-user") setGlobalErr(friendlyError(err?.code))
    } finally {
      setGoogleLoading(false)
    }
  }

  // ── onboarding step progression ──
  const totalSteps = 5
  const toStep = (s) => () => setStep(s)

  // ── Logo (mobile only, shown above form) ──
  const Logo = (
    <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
      <img src={resolutLogo} alt="Resolut" style={{ width: 32, height: 32, borderRadius: 7, objectFit: "contain", flexShrink: 0 }} />
      <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.025em", color: C.text, fontFamily: FF }}>
        Resolut
      </span>
    </Link>
  )

  // ── Right-panel content ──
  let content
  if (step === STEP.email) {
    content = (
      <SignupScaffold logo={Logo} isMobile={isMobile} step={0}>
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 32, fontWeight: 700, color: C.text, margin: 0, letterSpacing: "-0.025em", lineHeight: 1.1 }}>
            Create your{"\n"}account.
          </h1>
          <p style={{ fontSize: 14, color: C.textMuted, margin: "8px 0 0", lineHeight: 1.5 }}>
            Start your journey with Resolut.
          </p>
        </div>

        {globalErr && <ErrBanner msg={globalErr} />}

        <form onSubmit={handleEmailSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <LightInput
            label="Email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: "" })) }}
            autoComplete="email"
            error={errors.email}
            required
          />
          <AccentBtn type="submit" loading={loading}>Send verification code</AccentBtn>

          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "4px 0" }}>
            <div style={{ flex: 1, height: 1, background: C.border }} />
            <span style={{ fontSize: 12, color: C.textDim, fontWeight: 500 }}>or sign up with</span>
            <div style={{ flex: 1, height: 1, background: C.border }} />
          </div>

          <GhostBtn onClick={handleGoogle} disabled={googleLoading}>
            {googleLoading ? <Spinner color={C.text} /> : <GoogleIcon />}
            {googleLoading ? "Connecting…" : "Continue with Google"}
          </GhostBtn>

          <p style={{
            margin: "16px 0 0", fontSize: 13.5, fontWeight: 500,
            color: C.textMuted, textAlign: "center",
          }}>
            Already have an account?{" "}
            <Link to="/login" style={{ color: C.text, fontWeight: 600, textDecoration: "none" }}>
              Sign in
            </Link>
          </p>

          <p style={{ margin: "6px 0 0", textAlign: "center", fontSize: 11, color: C.textFaint, lineHeight: 1.6 }}>
            By continuing you agree to our Privacy Policy and Terms of Service.
          </p>
        </form>
      </SignupScaffold>
    )
  } else if (step === STEP.otp) {
    content = (
      <SignupScaffold
        logo={Logo}
        isMobile={isMobile}
        step={1}
        onBack={() => { setOtp(""); setErrors({}); setGlobalErr(""); setStep(STEP.email) }}
      >
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontSize: 32, fontWeight: 700, color: C.text, margin: 0, letterSpacing: "-0.025em", lineHeight: 1.1 }}>
            Check your{"\n"}inbox.
          </h1>
          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.accent }} />
            <span style={{ fontSize: 13, color: C.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              Code sent to {email}
            </span>
          </div>
        </div>

        {globalErr && <ErrBanner msg={globalErr} />}

        <div style={{ marginBottom: 20 }}>
          <OtpInput value={otp} onChange={(v) => { setOtp(v); setErrors((p) => ({ ...p, otp: "" })) }} onComplete={(v) => handleOtpVerify(v)} />
          {errors.otp && (
            <p style={{ margin: "6px 0 0", fontSize: 12, color: C.errorInk }}>{errors.otp}</p>
          )}
        </div>

        <AccentBtn loading={loading} onClick={() => handleOtpVerify()}>Verify code</AccentBtn>

        <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 14 }}>
          <button
            type="button"
            onClick={handleResend}
            disabled={retrySeconds > 0}
            style={{
              background: "none", border: "none", padding: 0,
              fontSize: 13, fontWeight: 600, fontFamily: FF,
              color: retrySeconds > 0 ? C.textFaint : C.text,
              cursor: retrySeconds > 0 ? "default" : "pointer",
            }}
          >
            {retrySeconds > 0 ? `Resend in ${retrySeconds}s` : "Resend code"}
          </button>
          <span style={{ color: C.textFaint }}>·</span>
          <button
            type="button"
            onClick={() => { setOtp(""); setGlobalErr(""); setStep(STEP.email) }}
            style={{ background: "none", border: "none", padding: 0, fontSize: 13, fontWeight: 600, color: C.textMuted, cursor: "pointer", fontFamily: FF }}
          >
            Change email
          </button>
        </div>
      </SignupScaffold>
    )
  } else if (step === STEP.password) {
    content = (
      <SignupScaffold
        logo={Logo}
        isMobile={isMobile}
        step={2}
        onBack={() => { setErrors({}); setGlobalErr(""); setStep(STEP.otp) }}
      >
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontSize: 32, fontWeight: 700, color: C.text, margin: 0, letterSpacing: "-0.025em", lineHeight: 1.1 }}>
            Almost{"\n"}done.
          </h1>
          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.accent }} />
            <span style={{ fontSize: 13, color: C.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {email} · verified
            </span>
          </div>
        </div>

        {globalErr && <ErrBanner msg={globalErr} />}

        <form onSubmit={handlePasswordSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <LightInput
            label="Password"
            type={showPw ? "text" : "password"}
            placeholder="Min 6 characters"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setErrors((p) => ({ ...p, password: "" })) }}
            autoComplete="new-password"
            error={errors.password}
            required
            right={
              <button type="button" onClick={() => setShowPw((s) => !s)}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                  background: "none", border: "none", cursor: "pointer", padding: 6,
                  display: "flex", alignItems: "center", color: C.textDim, borderRadius: 6 }}
                onMouseEnter={(e) => e.currentTarget.style.color = C.text}
                onMouseLeave={(e) => e.currentTarget.style.color = C.textDim}
              >
                {showPw ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            }
          />
          <LightInput
            label="Confirm password"
            type={showPw ? "text" : "password"}
            placeholder="Re-enter password"
            value={confirmPw}
            onChange={(e) => { setConfirm(e.target.value); setErrors((p) => ({ ...p, confirm: "" })) }}
            autoComplete="new-password"
            error={errors.confirm}
            required
          />
          <AccentBtn type="submit" loading={loading} disabled={password.length < 6 || !confirmPw || confirmPw !== password}>
            Create account
          </AccentBtn>

          <p style={{
            margin: "16px 0 0", fontSize: 13.5, fontWeight: 500,
            color: C.textMuted, textAlign: "center",
          }}>
            Already have an account?{" "}
            <Link to="/login" style={{ color: C.text, fontWeight: 600, textDecoration: "none" }}>
              Sign in
            </Link>
          </p>
        </form>
      </SignupScaffold>
    )
  } else if (step === STEP.resume) {
    content = (
      <OnboardingScaffold logo={Logo} isMobile={isMobile}>
        <ResumeUploadStep
          onContinue={toStep(STEP.howHeard)}
          onBack={() => setStep(STEP.password)}
        />
      </OnboardingScaffold>
    )
  } else if (step === STEP.howHeard) {
    content = (
      <OnboardingScaffold logo={Logo} isMobile={isMobile}>
        <HowHeardStep
          step={1}
          totalSteps={totalSteps}
          onContinue={toStep(STEP.jobSearch)}
          onBack={() => setStep(STEP.resume)}
        />
      </OnboardingScaffold>
    )
  } else if (step === STEP.jobSearch) {
    content = (
      <OnboardingScaffold logo={Logo} isMobile={isMobile}>
        <JobSearchStatusStep
          step={2}
          totalSteps={totalSteps}
          onContinue={toStep(STEP.location)}
          onBack={() => setStep(STEP.howHeard)}
        />
      </OnboardingScaffold>
    )
  } else if (step === STEP.location) {
    content = (
      <OnboardingScaffold logo={Logo} isMobile={isMobile}>
        <LocationStep
          step={3}
          totalSteps={totalSteps}
          onContinue={toStep(STEP.contact)}
          onBack={() => setStep(STEP.jobSearch)}
        />
      </OnboardingScaffold>
    )
  } else if (step === STEP.contact) {
    content = (
      <OnboardingScaffold logo={Logo} isMobile={isMobile}>
        <ContactDetailsStep
          step={4}
          totalSteps={totalSteps}
          onContinue={toStep(STEP.voluntary)}
          onBack={() => setStep(STEP.location)}
        />
      </OnboardingScaffold>
    )
  } else if (step === STEP.voluntary) {
    content = (
      <OnboardingScaffold logo={Logo} isMobile={isMobile}>
        <VoluntaryDisclosuresStep
          step={5}
          totalSteps={totalSteps}
          onContinue={toStep(STEP.complete)}
          onBack={() => setStep(STEP.contact)}
        />
      </OnboardingScaffold>
    )
  } else if (step === STEP.complete) {
    content = (
      <OnboardingScaffold logo={Logo} isMobile={isMobile}>
        <WelcomeCompleteStep onComplete={() => navigate("/dashboard/home")} />
      </OnboardingScaffold>
    )
  }

  return (
    <div style={{
      minHeight: "100vh", display: "flex", background: C.bg,
      fontFamily: FF, color: C.text,
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600;700&display=swap');
        @keyframes rg-spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        input::placeholder { color: ${C.textFaint}; }
        input:disabled { opacity: 0.5; cursor: not-allowed; }
      `}</style>

      {!isMobile && <AuthLeftPanel />}
      {content}
    </div>
  )
}

/* ── Right-panel container wrappers ──────────────────────────────── */

function SignupScaffold({ children, logo, isMobile, step, onBack }) {
  return (
    <div style={{
      flex: 1,
      display: "flex",
      alignItems: isMobile ? "flex-start" : "center",
      justifyContent: "center",
      padding: isMobile ? "32px 24px 48px" : "56px 72px",
      overflowY: "auto",
      background: C.bg,
    }}>
      <div style={{ width: "100%", maxWidth: 420 }}>
        {/* Mobile top bar: logo + 3 dots */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 32,
        }}>
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              aria-label="Back"
              style={{
                width: 36, height: 36, borderRadius: "50%",
                background: C.card, border: `1px solid ${C.border}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", color: C.text,
              }}
            >
              <svg width="13" height="13" viewBox="0 0 20 20" fill="none">
                <path d="M12 4l-6 6 6 6" stroke={C.text} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          ) : (
            isMobile ? logo : <div style={{ width: 36, height: 36 }} />
          )}
          <SignupDots step={step} />
        </div>
        {children}
      </div>
    </div>
  )
}

function OnboardingScaffold({ children, logo, isMobile }) {
  return (
    <div style={{
      flex: 1,
      display: "flex",
      alignItems: isMobile ? "flex-start" : "center",
      justifyContent: "center",
      padding: isMobile ? "32px 24px 48px" : "56px 72px",
      overflowY: "auto",
      background: C.bg,
    }}>
      <div style={{ width: "100%", maxWidth: 480 }}>
        {isMobile && <div style={{ marginBottom: 24 }}>{logo}</div>}
        {children}
      </div>
    </div>
  )
}
